import { buttonPortId } from "@velizhanin-dev/bot-scenario"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import type { DraftEntryLink, DraftFlow, NodeStat } from "@/entities/bot-builder"
import { getFlowStats, statsKeys } from "@/entities/bot-stats"
import { UtmFilter } from "@/features/manage-utm-links"
import { Send } from "lucide-react"
import { rememberRecentItem } from "@/shared/lib/recent-items"
import { botPath, PublishButton, useBotWorkspace, WithDraft } from "@/widgets/bot-workspace"
import { Editor } from "@/widgets/scenario-editor"
import { type BlockPeople, BlockPeoplePanel } from "./block-people-panel"

/** С чем открыть редактор: показать блок и сразу его людей (из списка воронок или поиска). */
export interface FunnelEditorState {
	focusNodeId?: string
	people?: BlockPeople
}

/**
 * Ссылка, по которой сотрудник пройдёт воронку в Telegram, как пользователь: основная
 * открывается /start, остальные через включённую UTM-метку. Идёт опубликованная версия.
 */
function funnelTelegramLink(
	username: string,
	flow: DraftFlow,
	links: DraftEntryLink[],
): { href: string; hint: string } | null {
	const base = `https://t.me/${username}`
	if (flow.kind === "MAIN") return { href: base, hint: "Откроется бот, нажмите «Старт». Идёт опубликованная версия" }
	const link = links.find((item) => item.flowId === flow.id && item.active)
	return link
		? {
				href: `${base}?start=${encodeURIComponent(link.code)}`,
				hint: `Откроется бот по метке ${link.code}. Идёт опубликованная версия, ваш вход попадёт в статистику метки`,
			}
		: null
}

/**
 * «Воронка: …»: редактор одной воронки внутри раздела бота, со статистикой на блоках.
 * Открывается на весь экран; шапка одна: «Назад», название, «Включить», публикация.
 */
export function FunnelEditorPage() {
	const { flowId = "" } = useParams()
	const { bot, draft } = useBotWorkspace()
	const navigate = useNavigate()
	const location = useLocation()
	const navState = location.state as FunnelEditorState | null
	const focusNodeId = navState?.focusNodeId

	const [searchParams, setSearchParams] = useSearchParams()
	const utm = searchParams.get("utm") ?? ""
	const suffix = utm ? `?utm=${encodeURIComponent(utm)}` : ""

	const stats = useQuery({
		queryKey: statsKeys.flows(bot.id, utm),
		queryFn: () => getFlowStats(bot.id, utm || undefined),
		refetchInterval: 60_000,
	})
	// Клик по «Прошли» или «Застряли» на блоке открывает панель с этими людьми.
	const [people, setPeople] = useState<BlockPeople | null>(navState?.people ?? null)
	// Панель из перехода открываем один раз: после перезагрузки или «Назад» она не всплывает снова.
	useEffect(() => {
		if (!navState?.people) return
		setPeople(navState.people)
		navigate(
			{ pathname: location.pathname, search: location.search },
			{ replace: true, state: { focusNodeId: navState.focusNodeId } },
		)
	}, [navState?.people, navState?.focusNodeId, navigate, location.pathname, location.search])
	const flow = draft?.draft?.flows.find((item) => item.id === flowId)
	const flowName = flow?.name ?? ""
	useEffect(() => {
		if (!flowName) return
		rememberRecentItem({
			kind: "funnel",
			id: flowId,
			label: flowName,
			to: botPath(bot.id, `constructor/funnels/${flowId}`),
		})
	}, [bot.id, flowId, flowName])
	const telegramLink = flow ? funnelTelegramLink(bot.username, flow, draft?.draft?.entryLinks ?? []) : null
	const openPeople = (nodeId: string) => (mode: "passed" | "stuck", label: string) =>
		setPeople({ nodeId, mode, label })
	const nodeStats = useMemo(() => {
		const byNode = new Map<string, NodeStat>(
			(stats.data?.nodes ?? []).map((row) => [
				row.nodeId,
				{ passed: row.passed, stuck: row.stuck, clicks: {}, openPeople: openPeople(row.nodeId) },
			]),
		)
		for (const row of stats.data?.buttons ?? []) {
			const stat = byNode.get(row.nodeId) ?? {
				passed: 0,
				stuck: 0,
				clicks: {},
				openPeople: openPeople(row.nodeId),
			}
			stat.clicks[buttonPortId(row.buttonId)] = row.clicks
			byNode.set(row.nodeId, stat)
		}
		return byNode
	}, [stats.data, utm])

	return (
		<WithDraft title="Воронка">
			{(state) => (
				<>
				<BlockPeoplePanel
					botId={bot.id}
					target={people}
					utm={utm}
					onModeChange={(mode) => setPeople((current) => (current ? { ...current, mode } : current))}
					onClose={() => setPeople(null)}
				/>
				<Editor
					key={flowId}
					draft={state.draft}
					actions={state.actions}
					isSaving={state.isSaving}
					whenSaved={state.whenSaved}
					serverLoad={state.serverLoad}
					embedded={{
						flowId,
						onBack: () => navigate(botPath(bot.id, `constructor/funnels${suffix}`)),
						onOpenFlow: (nextFlowId, nodeId) =>
							navigate(botPath(bot.id, `constructor/funnels/${nextFlowId}${suffix}`), {
								state: nodeId ? { focusNodeId: nodeId } : null,
							}),
						focusNodeId,
						nodeStats,
						menuExtra: telegramLink
							? [
									{
										label: "Пройти в Telegram",
										icon: Send,
										onSelect: () => window.open(telegramLink.href, "_blank", "noopener"),
									},
								]
							: [],
						canvasStart: draft ? (
								<UtmFilter
									links={state.draft.entryLinks ?? []}
									value={utm}
									onChange={(code) =>
										setSearchParams(code ? { utm: code } : {}, { replace: true })
									}
									className="h-9 w-40"
								/>
						) : null,
						headerEnd: draft ? (
							<>
								<PublishButton bot={bot} draft={draft} compact />
							</>
						) : null,
					}}
				/>
				</>
			)}
		</WithDraft>
	)
}
